import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import * as z from "zod";
import type { ProcessingAutomation } from "../processing-automation";
import type { ReturnsOperationsSettingsSource } from "./operations-settings-source";

/** When a compensation method is processed; COMPLETE means manually. */
export const PROCESSING_EVENTS = [
  ReturnStatus.OPEN,
  ReturnStatus.IN_TRANSIT,
  ReturnStatus.DELIVERED,
  ReturnStatus.COMPLETE,
] as const;
export type ProcessingEvent = (typeof PROCESSING_EVENTS)[number];

const PROCESSING_EVENT_SET: ReadonlySet<ReturnStatus> = new Set(
  PROCESSING_EVENTS,
);

function isProcessingEvent(status: ReturnStatus): status is ProcessingEvent {
  return PROCESSING_EVENT_SET.has(status);
}

const ProcessingEventsSchema = z.object({
  exchange: z.enum(PROCESSING_EVENTS),
  refund: z.enum(PROCESSING_EVENTS),
  storeCredit: z.enum(PROCESSING_EVENTS),
  exchangeDelay: z.number().int().min(0),
  refundDelay: z.number().int().min(0),
  storeCreditDelay: z.number().int().min(0),
});

export type ProcessingEvents = z.infer<typeof ProcessingEventsSchema>;

/** Both stored on `team.automation`, outside `settings`; the aggregator writes them. */
export const processingEventsSettingsShape = {
  returnProcessing: ProcessingEventsSchema,
  claimProcessing: ProcessingEventsSchema,
};

export type ProcessingEventsSettings = z.infer<
  z.ZodObject<typeof processingEventsSettingsShape>
>;

/** With `automation.enable` off nothing auto-processes, so every method reads as manual. */
function processingEventsFromStored(
  automationEnabled: boolean,
  stored: ProcessingAutomation | null | undefined,
): ProcessingEvents {
  const event = (status: ReturnStatus | undefined): ProcessingEvent => {
    const value = status ?? ReturnStatus.DELIVERED;
    return automationEnabled && isProcessingEvent(value)
      ? value
      : ReturnStatus.COMPLETE;
  };
  return {
    exchange: event(stored?.exchanges),
    refund: event(stored?.refunds),
    storeCredit: event(stored?.store_credit),
    exchangeDelay: Math.round(stored?.exchanges_delay ?? 0),
    refundDelay: Math.round(stored?.refunds_delay ?? 0),
    storeCreditDelay: Math.round(stored?.store_credit_delay ?? 0),
  };
}

export function processingEventsToStored(
  events: ProcessingEvents,
): ProcessingAutomation {
  return {
    exchanges: events.exchange,
    refunds: events.refund,
    store_credit: events.storeCredit,
    exchanges_delay: events.exchangeDelay,
    refunds_delay: events.refundDelay,
    store_credit_delay: events.storeCreditDelay,
  };
}

export function processingEventsSettingsFromTeam({
  automation,
}: ReturnsOperationsSettingsSource): ProcessingEventsSettings {
  const automationEnabled = automation?.enable ?? true;
  return {
    returnProcessing: processingEventsFromStored(
      automationEnabled,
      automation?.returnProcessing,
    ),
    claimProcessing: processingEventsFromStored(
      automationEnabled,
      automation?.claimProcessing,
    ),
  };
}
